import React, { useEffect, useRef, useState } from "react";
import { Loader2, Video, Square, RotateCcw, CheckCircle2 } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { useTranslation } from "react-i18next";

const MAX_SEC = 15;

export default function VideoRecorder({ open, onClose, onConfirm, uploading }) {
  const { t } = useTranslation();
  const [stream, setStream] = useState(null);
  const [recording, setRecording] = useState(false);
  const [seconds, setSeconds] = useState(0);
  const [blobUrl, setBlobUrl] = useState(null);
  const [error, setError] = useState(false);
  const blobRef = useRef(null);
  const liveRef = useRef(null);
  const recRef = useRef(null);
  const timerRef = useRef(null);
  const mimeRef = useRef("video/webm");

  useEffect(() => {
    if (!open) return;
    let s;
    let cancelled = false;
    (async () => {
      try {
        s = await navigator.mediaDevices.getUserMedia({ video: true, audio: true });
        if (cancelled) { s.getTracks().forEach((t) => t.stop()); return; }
        setStream(s);
        if (liveRef.current) liveRef.current.srcObject = s;
      } catch { setError(true); }
    })();
    return () => {
      cancelled = true;
      clearInterval(timerRef.current);
      try { if (recRef.current?.state === "recording") recRef.current.stop(); } catch { /* ignore */ }
      s?.getTracks().forEach((t) => t.stop());
      setStream(null); setRecording(false); setSeconds(0); setError(false);
      if (blobRef.current?.url) URL.revokeObjectURL(blobRef.current.url);
      blobRef.current = null; setBlobUrl(null);
    };
  }, [open]);

  const stop = () => {
    clearInterval(timerRef.current);
    try { recRef.current?.stop(); } catch { /* ignore */ }
    setRecording(false);
  };

  const start = () => {
    if (!stream) return;
    if (blobRef.current?.url) URL.revokeObjectURL(blobRef.current.url);
    blobRef.current = null; setBlobUrl(null); setSeconds(0);
    const mime = MediaRecorder.isTypeSupported("video/webm") ? "video/webm" : "video/mp4";
    mimeRef.current = mime;
    const rec = new MediaRecorder(stream, { mimeType: mime });
    const chunks = [];
    rec.ondataavailable = (e) => { if (e.data.size) chunks.push(e.data); };
    rec.onstop = () => {
      const b = new Blob(chunks, { type: mime });
      const url = URL.createObjectURL(b);
      blobRef.current = { blob: b, url };
      setBlobUrl(url);
    };
    recRef.current = rec;
    rec.start();
    setRecording(true);
    timerRef.current = setInterval(() => {
      setSeconds((x) => {
        if (x + 1 >= MAX_SEC) { stop(); return MAX_SEC; }
        return x + 1;
      });
    }, 1000);
  };

  const confirm = () => {
    if (!blobRef.current) return;
    const ext = mimeRef.current.includes("mp4") ? "mp4" : "webm";
    onConfirm(new File([blobRef.current.blob], `recording.${ext}`, { type: mimeRef.current }));
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && !uploading && onClose()}>
      <DialogContent className="max-w-lg bg-card" data-testid="video-recorder-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-foreground pr-8"><Video className="h-4 w-4 text-orange-600" /> {t("client.recordTitle", { sec: MAX_SEC })}</DialogTitle>
        </DialogHeader>
        {error ? (
          <p className="text-sm text-muted-foreground py-8 text-center" data-testid="vr-error">{t("client.cameraDenied")}</p>
        ) : (
          <div className="space-y-3">
            <div className="rounded-md bg-black/90 aspect-video grid place-items-center overflow-hidden">
              {blobUrl
                ? <video src={blobUrl} controls className="w-full h-full" data-testid="vr-preview" />
                : <video ref={liveRef} autoPlay muted playsInline className="w-full h-full" data-testid="vr-live" />}
            </div>
            <div className="flex items-center justify-between gap-2">
              <span className="font-mono text-sm text-muted-foreground" data-testid="vr-timer">0:{String(seconds).padStart(2, "0")} / 0:{MAX_SEC}</span>
              {blobUrl ? (
                <div className="flex gap-2">
                  <Button size="sm" variant="outline" onClick={start} disabled={uploading} className="rounded-sm border-slate-300 bg-card text-xs" data-testid="vr-retake"><RotateCcw className="h-3.5 w-3.5 mr-1" /> {t("client.reRecord")}</Button>
                  <Button size="sm" onClick={confirm} disabled={uploading} className="rounded-sm bg-orange-500 text-white hover:bg-orange-600 text-xs" data-testid="vr-confirm">
                    {uploading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <><CheckCircle2 className="h-3.5 w-3.5 mr-1" /> {t("client.useVideo")}</>}
                  </Button>
                </div>
              ) : recording ? (
                <Button size="sm" onClick={stop} className="rounded-sm bg-red-600 text-white hover:bg-red-700 text-xs" data-testid="vr-stop"><Square className="h-3.5 w-3.5 mr-1" /> {t("client.stop")}</Button>
              ) : (
                <Button size="sm" onClick={start} disabled={!stream} className="rounded-sm bg-orange-500 text-white hover:bg-orange-600 text-xs" data-testid="vr-start"><Video className="h-3.5 w-3.5 mr-1" /> {t("client.startRecording")}</Button>
              )}
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
